import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, X, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { Drawer, useToast } from "@/components/system";
import {
  FormInput,
  PrimaryButton,
  SecondaryButton,
  DialogFooter,
} from "@/components/forms";
import { formatError } from "@/lib/format-error";

/**
 * BW9 — Polymorphic project_item editor (SPEC 3.4).
 *
 * Renders one of six primitive forms based on `kind`:
 *   milestone (Timeline) | decision | task (Checklist) | reference | note | log
 *
 * Stores the kind-specific fields in `project_item.data` jsonb. The schema
 * already accepts every kind we use here; the editor just shapes the
 * `data` payload.
 */

export type ProjectItemPrimitive =
  | "milestone"
  | "decision"
  | "task"
  | "reference"
  | "note"
  | "log";

export type ProjectItemRow = {
  id: string;
  project_id: string;
  module_label: string | null;
  title: string;
  kind: string;
  data: Record<string, unknown> | null;
  notes: string | null;
  order_index: number | null;
};

const PRIMITIVES: Array<{ key: ProjectItemPrimitive; label: string; hint: string }> = [
  { key: "milestone", label: "Timeline", hint: "A dated milestone (planned, done, blocked)" },
  { key: "task", label: "Checklist", hint: "A list of items you can tick off" },
  { key: "decision", label: "Decision", hint: "A question with options + rationale" },
  { key: "reference", label: "Reference", hint: "Link out to a doc, paper, or URL" },
  { key: "note", label: "Note", hint: "Free-form markdown" },
  { key: "log", label: "Log", hint: "Timestamped entry / journal" },
];

export function AddProjectItemButton({ projectId }: { projectId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "6px 10px",
          background: "transparent",
          color: "var(--accent)",
          border: "1px solid var(--accent-dim)",
          borderRadius: "var(--radius-chip)",
          fontSize: "var(--type-meta)",
          cursor: "pointer",
        }}
      >
        <Plus size={12} strokeWidth={1.8} /> Add item
      </button>
      {open && (
        <ProjectItemDrawer
          projectId={projectId}
          existing={null}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export function EditProjectItemButton({ row }: { row: ProjectItemRow }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          background: "transparent",
          border: "none",
          color: "var(--fg-3)",
          fontSize: "var(--type-meta)",
          cursor: "pointer",
          padding: 0,
        }}
        title="Edit item"
      >
        edit
      </button>
      {open && (
        <ProjectItemDrawer
          projectId={row.project_id}
          existing={row}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function ProjectItemDrawer({
  projectId,
  existing,
  onClose,
}: {
  projectId: string;
  existing: ProjectItemRow | null;
  onClose: () => void;
}) {
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();
  const toast = useToast();

  const isEdit = existing != null;
  const [kind, setKind] = useState<ProjectItemPrimitive>(
    (existing?.kind as ProjectItemPrimitive) ?? "milestone",
  );
  const [title, setTitle] = useState(existing?.title ?? "");
  const [data, setData] = useState<Record<string, unknown>>(
    (existing?.data ?? {}) as Record<string, unknown>,
  );

  function patchData(patch: Record<string, unknown>) {
    setData((d) => ({ ...d, ...patch }));
  }

  const save = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Not signed in");
      if (isEdit) {
        const { error } = await supabase
          .from("project_item")
          .update({ kind, title: title.trim() || "Untitled", data: data as never })
          .eq("id", existing!.id)
          .eq("user_id", userId);
        if (error) throw error;
      } else {
        // Generate a stable text id; timestamp + random suffix keeps it sortable
        const id = `${projectId}__${kind}_${Date.now().toString(36)}`;
        const { error } = await supabase.from("project_item").insert({
          id,
          user_id: userId,
          project_id: projectId,
          kind,
          title: title.trim() || "Untitled",
          data: data as never,
          order_index: 999,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project_items", userId, projectId] });
      toast.show(isEdit ? "Item updated." : "Item added.");
      onClose();
    },
    onError: (e) => toast.show(formatError(e)),
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (!existing || !userId) return;
      const { error } = await supabase
        .from("project_item")
        .delete()
        .eq("id", existing.id)
        .eq("user_id", userId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project_items", userId, projectId] });
      toast.show("Item deleted.");
      onClose();
    },
    onError: (e) => toast.show(formatError(e)),
  });

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title={isEdit ? "Edit item" : "Add project item"}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
      >
        {!isEdit && (
          <KindPicker value={kind} onChange={setKind} />
        )}

        <FormInput
          label="Title"
          value={title}
          onChange={setTitle}
          placeholder={kindPlaceholder(kind)}
          autoFocus
        />

        <KindForm kind={kind} data={data} patch={patchData} />

        <DialogFooter>
          {isEdit && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm("Delete this item?")) remove.mutate();
              }}
              disabled={remove.isPending}
              style={{
                background: "transparent",
                color: "var(--brick)",
                border: "1px solid var(--brick)",
                borderRadius: "var(--radius-chip)",
                padding: "6px 10px",
                fontSize: "var(--type-body-s)",
                cursor: "pointer",
                marginRight: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Trash2 size={12} strokeWidth={1.6} /> Delete
            </button>
          )}
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton type="submit" disabled={save.isPending}>
            {save.isPending ? "Saving…" : isEdit ? "Save" : "Add"}
          </PrimaryButton>
        </DialogFooter>
      </form>
    </Drawer>
  );
}

function KindPicker({
  value,
  onChange,
}: {
  value: ProjectItemPrimitive;
  onChange: (k: ProjectItemPrimitive) => void;
}) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
      {PRIMITIVES.map((p) => {
        const active = p.key === value;
        return (
          <button
            key={p.key}
            type="button"
            onClick={() => onChange(p.key)}
            title={p.hint}
            style={{
              padding: "8px 6px",
              background: active ? "var(--accent-wash)" : "var(--bg-3)",
              color: active ? "var(--accent)" : "var(--fg-2)",
              border: `1px solid ${active ? "var(--accent-dim)" : "var(--hair-2)"}`,
              borderRadius: "var(--radius-chip)",
              fontSize: "var(--type-body-s)",
              cursor: "pointer",
            }}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

function KindForm({
  kind,
  data,
  patch,
}: {
  kind: ProjectItemPrimitive;
  data: Record<string, unknown>;
  patch: (p: Record<string, unknown>) => void;
}) {
  switch (kind) {
    case "milestone":
      return (
        <>
          <FormInput
            label="Date"
            type="date"
            value={(data.date as string) ?? ""}
            onChange={(v) => patch({ date: v })}
          />
          <Select
            label="Status"
            value={(data.status as string) ?? "planned"}
            onChange={(v) => patch({ status: v })}
            options={[
              { value: "planned", label: "Planned" },
              { value: "in_progress", label: "In progress" },
              { value: "done", label: "Done" },
              { value: "blocked", label: "Blocked" },
            ]}
          />
          <FormInput
            label="Detail"
            value={(data.detail as string) ?? ""}
            onChange={(v) => patch({ detail: v })}
          />
        </>
      );
    case "task":
      return <ChecklistEditor data={data} patch={patch} />;
    case "decision":
      return (
        <>
          <FormInput
            label="Question"
            value={(data.question as string) ?? ""}
            onChange={(v) => patch({ question: v })}
            placeholder="Should we…?"
          />
          <DecisionOptionsEditor data={data} patch={patch} />
          <FormInput
            label="Decided on"
            type="date"
            value={(data.decided_at as string) ?? ""}
            onChange={(v) => patch({ decided_at: v })}
          />
          <Textarea
            label="Rationale"
            value={(data.rationale as string) ?? ""}
            onChange={(v) => patch({ rationale: v })}
          />
        </>
      );
    case "reference":
      return (
        <>
          <FormInput
            label="URL"
            value={(data.url as string) ?? ""}
            onChange={(v) => patch({ url: v })}
            placeholder="https://…"
          />
          <Select
            label="Source type"
            value={(data.source_type as string) ?? "web"}
            onChange={(v) => patch({ source_type: v })}
            options={[
              { value: "web", label: "Web page" },
              { value: "paper", label: "Paper" },
              { value: "doc", label: "Document" },
              { value: "video", label: "Video" },
              { value: "other", label: "Other" },
            ]}
          />
          <FormInput
            label="Citation"
            value={(data.citation as string) ?? ""}
            onChange={(v) => patch({ citation: v })}
            placeholder="Plews 2013, J. Sports Sci."
          />
        </>
      );
    case "note":
      return (
        <Textarea
          label="Body"
          value={(data.body as string) ?? ""}
          onChange={(v) => patch({ body: v })}
          rows={8}
          placeholder="Markdown supported"
        />
      );
    case "log":
      return (
        <>
          <FormInput
            label="When"
            type="datetime-local"
            value={(data.ts as string) ?? localNow()}
            onChange={(v) => patch({ ts: v })}
          />
          <Textarea
            label="Entry"
            value={(data.body as string) ?? ""}
            onChange={(v) => patch({ body: v })}
            rows={5}
          />
        </>
      );
  }
}

function ChecklistEditor({
  data,
  patch,
}: {
  data: Record<string, unknown>;
  patch: (p: Record<string, unknown>) => void;
}) {
  const items = ((data.items as Array<{ label: string; done: boolean }>) ?? []).slice();
  return (
    <section style={{ display: "grid", gap: "var(--space-2)" }}>
      <header
        style={{
          color: "var(--fg-3)",
          fontSize: "var(--type-meta)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        Items
      </header>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 4 }}>
        {items.map((it, i) => (
          <li key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={it.done}
              onChange={(e) => {
                const next = items.slice();
                next[i] = { ...it, done: e.target.checked };
                patch({ items: next });
              }}
            />
            <input
              type="text"
              value={it.label}
              onChange={(e) => {
                const next = items.slice();
                next[i] = { ...it, label: e.target.value };
                patch({ items: next });
              }}
              style={{
                flex: 1,
                background: "var(--bg-3)",
                color: "var(--fg)",
                border: "1px solid var(--hair-2)",
                borderRadius: "var(--radius-chip)",
                padding: "6px 10px",
                fontSize: "var(--type-body-s)",
              }}
            />
            <button
              type="button"
              onClick={() => patch({ items: items.filter((_, idx) => idx !== i) })}
              aria-label="Remove"
              style={{
                background: "transparent",
                border: "none",
                color: "var(--fg-3)",
                cursor: "pointer",
                padding: 4,
              }}
            >
              <X size={14} strokeWidth={1.6} />
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={() => patch({ items: [...items, { label: "", done: false }] })}
        style={{
          alignSelf: "flex-start",
          background: "transparent",
          color: "var(--accent)",
          border: "none",
          padding: 0,
          fontSize: "var(--type-meta)",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <Plus size={12} strokeWidth={1.8} /> Add row
      </button>
    </section>
  );
}

function DecisionOptionsEditor({
  data,
  patch,
}: {
  data: Record<string, unknown>;
  patch: (p: Record<string, unknown>) => void;
}) {
  const options = ((data.options as string[]) ?? []).slice();
  const chosen = (data.chosen_option as string) ?? "";
  return (
    <section style={{ display: "grid", gap: "var(--space-2)" }}>
      <header
        style={{
          color: "var(--fg-3)",
          fontSize: "var(--type-meta)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        Options · pick one
      </header>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 4 }}>
        {options.map((opt, i) => (
          <li key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="radio"
              name="decision-chosen"
              checked={chosen === opt}
              onChange={() => patch({ chosen_option: opt })}
            />
            <input
              type="text"
              value={opt}
              onChange={(e) => {
                const next = options.slice();
                next[i] = e.target.value;
                patch({ options: next });
              }}
              style={{
                flex: 1,
                background: "var(--bg-3)",
                color: "var(--fg)",
                border: "1px solid var(--hair-2)",
                borderRadius: "var(--radius-chip)",
                padding: "6px 10px",
                fontSize: "var(--type-body-s)",
              }}
            />
            <button
              type="button"
              onClick={() => patch({ options: options.filter((_, idx) => idx !== i) })}
              aria-label="Remove"
              style={{
                background: "transparent",
                border: "none",
                color: "var(--fg-3)",
                cursor: "pointer",
                padding: 4,
              }}
            >
              <X size={14} strokeWidth={1.6} />
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={() => patch({ options: [...options, ""] })}
        style={{
          alignSelf: "flex-start",
          background: "transparent",
          color: "var(--accent)",
          border: "none",
          padding: 0,
          fontSize: "var(--type-meta)",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <Plus size={12} strokeWidth={1.8} /> Add option
      </button>
    </section>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", fontSize: "var(--type-body-s)", color: "var(--fg-2)" }}>
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          height: 38,
          padding: "0 var(--space-3)",
          background: "var(--bg-3)",
          color: "var(--fg)",
          border: "1px solid var(--hair-2)",
          borderRadius: "var(--radius-card)",
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-body)",
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Textarea({
  label,
  value,
  onChange,
  rows = 4,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", fontSize: "var(--type-body-s)", color: "var(--fg-2)" }}>
      {label}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        style={{
          padding: "var(--space-3)",
          background: "var(--bg-3)",
          color: "var(--fg)",
          border: "1px solid var(--hair-2)",
          borderRadius: "var(--radius-card)",
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-body)",
          resize: "vertical",
        }}
      />
    </label>
  );
}

function kindPlaceholder(kind: ProjectItemPrimitive): string {
  switch (kind) {
    case "milestone": return "Order bike travel bags";
    case "task": return "Pre-trip packing list";
    case "decision": return "Which bike bag brand?";
    case "reference": return "Strava route export tutorial";
    case "note": return "Stage 3 — climbing logistics";
    case "log": return "Wheels arrived from EVOC";
  }
}

function localNow(): string {
  const d = new Date();
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
